<template>
    <div class="apprentice-table-wrapper">
        <div
            class="apprentice-table-header"
            v-if="total_rows > 10 || columnSelect || !nosearch || hasOptionsSlot">
            <div
                class="apprentice-table-filters"
                v-if="total_rows > 10 || columnSelect || !nosearch || hasOptionsSlot"
                :class="`apprentice-table-stacked-at-${stacked}`">
                <div
                    class="search"
                    v-if="!nosearch">
                    <b-input-group>
                        <b-form-input
                            v-model="filter"
                            size="md"
                            debounce="300"
                            :placeholder="$t('common.search')"></b-form-input>
                        <b-input-group-append>
                            <div class="icon">
                                <i class="fas fa-search fa-fw"></i>
                            </div>
                        </b-input-group-append>
                    </b-input-group>
                </div>

                <div
                    class="page-records d-none d-sm-block"
                    v-if="total_rows > 10">
                    <label
                        class="sr-only"
                        for="per-page"
                        >{{ $t('common.per_page') }}</label
                    >
                    <b-form-select
                        class="apprentice-primary-select per-page-record-select"
                        id="per-page"
                        v-model="perPageSelected"
                        size="sm"
                        :options="perPageOptions"
                        @change="storePageSetting">
                    </b-form-select>
                </div>

                <div
                    class="column-filters"
                    v-if="columnSelect">
                    <b-dropdown
                        no-caret
                        variant="primary-icon"
                        toggle-class="filters-column-toggle"
                        menu-class="hide-columns-menu">
                        <template #button-content>
                            <i class="fad fa-fw fa-filter"></i>
                        </template>

                        <b-dropdown-header class="column-filters-dropdown-header-label"> Kolommen</b-dropdown-header>
                        <b-dropdown-divider></b-dropdown-divider>

                        <div
                            class="dropdown-item"
                            v-for="(field, index) in columns"
                            :key="`${field.key}-${index}`">
                            <b-form-checkbox
                                @change="hiddenColumnsChanged(field.key, $event)"
                                switch
                                :checked="field.hide"
                                :disabled="field.disabled">
                                {{ field.label ? field.label : field.key }}
                            </b-form-checkbox>
                        </div>
                    </b-dropdown>
                </div>
            </div>

            <div
                class="apprentice-data-table-actions options"
                v-if="hasOptionsSlot || (downloadExcelSettings.length && !hideDefaultExcelButton)">
                <slot name="options"></slot>
                <download-excel
                    class="download-as-excel"
                    v-if="downloadExcelSettings.length"
                    :data="downloadExcelData">
                    <b-button
                        class="btn-download btn-export-as-excel"
                        variant="primary">
                        {{ $t('common.download_excel_file') }}
                    </b-button>
                </download-excel>
            </div>
        </div>

        <div
            class="apprentice-table-secondary-header"
            v-if="hasSecondaryHeader">
            <slot name="secondaryHeader"></slot>
        </div>

        <b-table
            table-class="apprentice-data-table"
            ref="apprenticeDataTable"
            striped
            hover
            responsive
            :stacked="stacked"
            :fields="filteredFields"
            :items="items"
            :per-page="perPageSelected"
            :current-page="current_page"
            :filter="searchTerm !== null ? searchTerm : filter"
            :filter-ignored-fields="filterIgnoredFields"
            :filter-included-fields="filterIncludedFields"
            :filter-function="filterFunction ? filterFunction : customfilter"
            @filtered="filtered"
            :sort-compare="sortCompare"
            :sort-compare-options="{ numeric: true, sensitivity: 'base' }"
            sort-compare-locale="nl"
            :show-empty="showEmpty"
            :sort-by="sortBy"
            :sort-desc="sortDesc"
            @row-clicked="onRowClicked"
            @row-selected="onRowSelected"
            @context-changed="contextChanged">
            <template #head(column_selection)="data">
                <b-dropdown
                    class="ellipsis-nav bordered-menu crud-option-nav"
                    variant="primary-icon"
                    right
                    text="Right align"
                    no-caret
                    toggle-class="crud-option-context-menu">
                    <template #button-content>
                        <i class="fas fa-fw fa-ellipsis-v"></i>
                    </template>

                    <b-dropdown-item
                        class="crud-select-all"
                        v-if="selectedRows.length !== items.length"
                        @click.prevent.stop="selectAllRows">
                        {{ $t('common.select_all') }}
                    </b-dropdown-item>

                    <b-dropdown-item
                        class="crud-deselect-all"
                        v-if="selectedRows.length === items.length"
                        @click.prevent.stop="deSelectAllRows">
                        {{ $t('common.deselect_all') }}
                    </b-dropdown-item>

                    <template
                        v-for="slotName in contextMenuItemsSlots"
                        v-if="contextMenuItemsSlots.length">
                        <slot :name="`contextMenuItem(${slotName})`"></slot>
                    </template>
                </b-dropdown>
            </template>

            <template #cell(column_selection)="data">
                <div class="toggle-row">
                    <b-form-checkbox
                        :key="data.item.index"
                        :checked="getRowData(data)"
                        @change="toggleSelectRow($event, data)">
                    </b-form-checkbox>
                </div>
            </template>

            <!-- Pass on all scoped slots -->
            <template
                v-for="slot in Object.keys($scopedSlots)"
                :slot="slot"
                slot-scope="scope">
                <slot
                    v-bind="scope"
                    :name="slot" />
            </template>

            <template #custom-foot="data">
                <b-tr>
                    <b-th
                        class="tfoot-header"
                        :colspan="fields.length">
                        <div class="tfoot-bottom">
                            <div
                                class="apprentice-pagination"
                                v-if="total_rows > perPageSelected">
                                <b-pagination
                                    v-model="current_page"
                                    size="md"
                                    :total-rows="total_rows"
                                    :per-page="perPageSelected"
                                    first-class="go-to-first-page"
                                    last-class="go-to-last-page"
                                    next-class="nex-page"
                                    prev-class="previous-page"
                                    page-class="page">
                                    <template #first-text>
                                        <i class="far fa-fw fa-chevron-double-left"></i>
                                    </template>

                                    <template #prev-text>
                                        <i class="far fa-fw fa-chevron-left"></i>
                                    </template>

                                    <template #next-text>
                                        <i class="far fa-fw fa-chevron-right"></i>
                                    </template>

                                    <template #last-text>
                                        <i class="far fa-fw fa-chevron-double-right"></i>
                                    </template>
                                </b-pagination>
                            </div>

                            <div class="muted-text total-records">
                                {{ $t('components.total_rows', { total_rows }) }}
                            </div>
                        </div>
                    </b-th>
                </b-tr>
            </template>
        </b-table>

        <download-excel
            v-if="downloadExcelSettings.length && (!total_rows > 10 || !columnSelect || nosearch || !hasOptionsSlot)"
            :data="downloadExcelData">
            <b-button
                class="hidden"
                ref="download-excel-button">
                Download Excel-bestand
            </b-button>
        </download-excel>
    </div>
</template>

<script>
export default {
    name: 'design-2021-apprentice-data-table',
    props: {
        fields: {},

        items: {},

        filterIgnoredFields: {},

        filterIncludedFields: {
            type: Array,
            default: null,
        },

        nosearch: {
            type: Boolean,
            default: false,
        },

        columnSelect: {
            type: Boolean,
            default: false,
        },

        hideColumns: {
            type: Array,
            default: () => [],
        },

        sortCompare: {
            type: Function,
            default: () => false,
        },

        stacked: {
            type: [String, Boolean],
            default: () => 'sm',
        },

        formatFunc: {
            type: Function,
            default: () => false,
        },

        showEmpty: {
            type: Boolean,
            default: false,
        },

        customPerPage: Number,

        currentPage: {
            type: Number,
            required: false,
            default: 1,
        },

        filterFunction: {
            type: Function,
        },

        downloadExcelSettings: {
            type: Array,
            default: () => [],
        },

        columnsSelectionMode: {
            type: Boolean,
            required: false,
            default: false,
        },

        contextMenuItemsSlots: {
            type: Array,
            required: false,
            default: () => [],
        },

        searchTerm: {
            type: String,
            required: false,
            default: null,
        },
        sortBy: {
            type: String,
            required: false,
            default: null,
        },
        sortDesc: {
            type: Boolean,
            require: false,
            default: null,
        },
        hideDefaultExcelButton: {
            type: Boolean,
            required: false,
            default: false,
        },
    },

    data() {
        return {
            filter: null,
            current_page: this.currentPage,
            total_rows: this.items ? this.items.length : 0,
            perPageSelected: this.customPerPage ? this.customPerPage : 10,
            perPageOptions: [
                {
                    value: 10,
                    text: 10,
                },
                {
                    value: 25,
                    text: 25,
                },
                {
                    value: 50,
                    text: 50,
                },
                {
                    value: 100,
                    text: 100,
                },
                {
                    value: 200,
                    text: 200,
                },
                {
                    value: this.items ? this.items.length : 999,
                    text: this.$t('components.show_all'),
                },
            ],
            totalRows: 0,
            hiddenColumns: this.hideColumns,
            allRowsSelected: false,
            indeterminate: false,
            rows: [],
        };
    },

    watch: {
        hideColumns: function (hiddenColumns) {
            this.hiddenColumns = hiddenColumns;
        },

        items: function (newItems) {
            if (newItems) {
                this.recountPages(newItems.length);
            }

            this.rows = newItems.map((item, index) => {
                return {
                    index,
                    checked: false,
                };
            });
        },

        customPerPage: function (newVal) {
            this.perPageSelected = newVal;
        },
    },

    computed: {
        downloadExcelData() {
            // This is a template for how to use this feature.
            // downloadExcelSettings: [
            //     {
            //         name: 'id',
            //         value: 'id'
            //     },
            //     {
            //         name: 'uren',
            //         value: 'totalHours',
            //     },
            //     {
            //         name: 'naam',
            //         value: (data) => {
            //             return data.full_name + ' ' + data.completedBonusHours
            //         }
            //     }
            // ]

            if (!this.downloadExcelSettings.length) {
                return [];
            }

            return this.items.map((item) => {
                let returnItem = {};
                for (const setting of this.downloadExcelSettings) {
                    returnItem[setting.name.replace(/\./g, '')] =
                        typeof setting.value === 'function' ? setting.value(item) : item[setting.value];
                }

                return returnItem;
            });
        },

        hasOptionsSlot() {
            return this.$slots.options || this.$scopedSlots.options;
        },

        columns() {
            if (this.columnSelect) {
                let fields = this.fields.filter((field) => field.key !== 'actions');

                const disabled = fields.length - this.hiddenColumns.length < 2;

                return fields.map((field) => {
                    return {
                        ...field,
                        hide: this.hiddenColumns.some((column) => column === field.key),
                        disabled: disabled,
                    };
                });
            }
            return [];
        },

        filteredFields() {
            const fields = this.fields.filter((field) => !this.hiddenColumns.includes(field.key));

            const name = `table-${this._uid}-columns`;
            localStorage.setItem(name, JSON.stringify(fields));

            if (this.columnsSelectionMode) {
                fields.unshift({
                    key: 'column_selection',
                });
            }

            return fields;
        },

        selectedRows() {
            for (const [index, row] of this.rows.entries()) {
                if (row.checked) {
                    this.$refs['apprenticeDataTable'].selectRow(index);
                }
            }

            let rows = this.rows.filter((row) => row.checked);

            const selectedRows = rows.map((row) => {
                return this.items[row.index];
            });

            this.$emit('rows-selected', selectedRows);

            return rows;
        },

        hasSecondaryHeader() {
            return this.$slots.secondaryHeader || this.$scopedSlots.secondaryHeader;
        },
    },

    methods: {
        contextChanged(ctx) {
            this.$emit('context-changed', ctx);
        },

        hiddenColumnsChanged(key, value) {
            if (value) {
                this.hiddenColumns.push(value);
            } else {
                this.hiddenColumns = this.hiddenColumns.filter((column) => column !== key);
            }
        },

        filtered(items, count) {
            this.recountPages(count);
        },

        recountPages(count) {
            this.total_rows = count;
            let pages = Math.ceil(count / this.perPageSelected);
            if (pages < this.current_page) {
                this.current_page = 1;
            }
        },

        storePageSetting(checked) {
            const name = `table-${this._uid}-per-page`;
            localStorage[name] = checked;
        },

        customfilter(row, filter) {
            let found = false;
            for (const field of this.filteredFields) {
                if (this.filterIgnoredFields && this.filterIgnoredFields.indexOf(field.key) !== -1) {
                    continue;
                }

                if (
                    row[field.key] &&
                    row[field.key] !== null &&
                    row[field.key] !== '' &&
                    typeof row[field.key] !== 'object'
                ) {
                    const cleanRow = String(row[field.key])
                        .toLowerCase()
                        .normalize('NFD')
                        .replace(/\p{Diacritic}/gu, '');
                    const cleanFilter = filter
                        .toLowerCase()
                        .normalize('NFD')
                        .replace(/\p{Diacritic}/gu, '');
                    if (String(cleanRow).indexOf(cleanFilter) !== -1) {
                        found = true;
                        break;
                    }
                }
            }
            return found;
        },

        toggleSelectRow(checked, data) {
            const pos = (this.current_page - 1) * this.perPageSelected + data.index;

            if (checked) {
                this.rows[pos].checked = true;
                this.$refs['apprenticeDataTable'].selectRow(data.index);
            } else {
                this.rows[pos].checked = false;
                this.$refs['apprenticeDataTable'].unselectRow(data.index);
            }
        },

        onRowSelected(ev) {
            for (const [index, row] of this.rows.entries()) {
                if (row.checked) {
                    this.$refs['apprenticeDataTable'].selectRow(index);
                } else {
                    this.$refs['apprenticeDataTable'].unselectRow(index);
                }
            }
        },

        onRowClicked(item, index, event) {
            this.$emit('row-clicked', item, index, event);
            const pos = (this.current_page - 1) * this.perPageSelected + index;
            this.rows[pos].checked = !this.rows[pos].checked;
        },

        selectAllRows() {
            if (this.items.length > 0) {
                this.rows = this.items.map((item, index) => {
                    this.$refs['apprenticeDataTable'].selectRow(index);
                    return {
                        index,
                        checked: true,
                    };
                });
            }
        },

        deSelectAllRows() {
            this.rows = this.rows.map((row) => {
                this.$refs['apprenticeDataTable'].unselectRow(row.index);
                row.checked = false;
                return row;
            });
        },

        getRowData(data) {
            const pos = (this.current_page - 1) * this.perPageSelected + data.index;
            if (this.rows.length > 0 && this.rows[pos].checked) {
                return this.rows[pos].checked ? this.rows[pos].checked : false;
            }

            return false;
        },
    },

    mounted() {
        const perPageStorage = `table-${this._uid}-per-page`;
        const columnsStorage = `table-${this._uid}-columns`;

        if (localStorage[columnsStorage]) {
            const columnStorageItems = JSON.parse(localStorage.getItem(columnsStorage));
            if (columnStorageItems.length) {
                this.hiddenColumns = columnStorageItems;
            }
        }

        if (localStorage[perPageStorage]) {
            this.perPageSelected = parseInt(localStorage[perPageStorage]);

            if (this.customPerPage && this.customPerPage !== 0) {
                this.perPageSelected = this.customPerPage;
            }
        }

        this.rows = this.items.map((item, index) => {
            return {
                index,
                checked: false,
            };
        });
    },
};
</script>

<style scoped lang="scss">
.apprentice-table-header {
    &,
    .apprentice-table-filters,
    .apprentice-data-table-actions {
        display: flex;
    }

    flex-direction: row;
    flex: 1 0 100%;
    flex-wrap: wrap;
    justify-content: space-between;
    margin-bottom: map-get($grid-gutter-spacing, 'twenty-one');

    .apprentice-data-table-actions,
    .apprentice-table-filters {
        flex: 1 0 100%;
    }

    .apprentice-data-table-actions {
        flex-direction: column;

        > div:not(:last-child),
        > button:not(:last-child),
        > span:not(:last-child),
        > select:not(:last-child) {
            margin-bottom: map-get($grid-gutter-spacing, 'ten');
        }
    }

    .apprentice-table-filters {
        margin-bottom: map-get($grid-gutter-spacing, 'ten');

        .search {
            width: 100%;

            .input-group {
                &:focus-within {
                    box-shadow: 0 0 0 0.2rem rgba(var(--primary), 25%);
                    border-radius: $border-radius-content;
                }

                input {
                    &:focus {
                        box-shadow: none;
                    }
                }
            }

            .input-group-append {
                color: var(--primary);
                align-items: center;
                background-color: $secondary-background-color;
                padding: map-get($grid-gutter-spacing, 'fifteen') map-get($grid-gutter-spacing, 'ten');
                border-top-right-radius: $border-radius-content;
                border-bottom-right-radius: $border-radius-content;
            }
        }
    }

    .column-filters ::v-deep {
        margin-right: map-get($grid-gutter-spacing, 'ten');

        .hide-columns-menu {
            padding: 0;

            .dropdown-header {
                padding: map-get($grid-gutter-spacing, 'ten') map-get($grid-gutter-spacing, 'twenty-one');
                font-size: $base-font-size;
                font-weight: $font-weight-medium;
                color: var(--primary);
                height: auto;
            }

            .dropdown-divider {
                margin: 0;
            }
        }
    }
}

.apprentice-table-wrapper {
    .table-responsive {
        margin-bottom: 0;
        min-height: 225px;
    }
}

.download-as-excel {
    width: 100%;
    margin-left: 0;

    .btn-export-as-excel {
        width: 100%;
    }
}

::v-deep .apprentice-data-table {
    .crud-option-context-menu {
        &,
        &:not(:disabled):not(.disabled) {
            background-color: var(--primary);
            color: $white !important;

            &:active {
                background-color: var(--primaryLightest);
                color: $white !important;
            }
        }
    }

    td {
        font-size: $default-font-size;
    }

    th,
    tbody tr td {
        border: none;
        vertical-align: middle;
    }

    tbody {
        tr {
            &:nth-of-type(odd) {
                background-color: $secondary-background-color;
            }

            &.b-table-row-selected.table-active,
            &:hover {
                background-color: var(--primaryLightest);
            }
        }

        tr.b-table-empty-row {
            background-color: transparent;

            .not-found {
                margin-top: map-get($grid-gutter-spacing, 'twenty-one') * 2;
            }
        }

        .toggle-row {
            width: 34px;
            text-align: right;
        }

        input,
        select {
            border: 1px solid $border-color;
        }

        .btn {
            font-size: 1.3125rem;

            > span,
            > div {
                font-size: initial;
            }
        }
    }

    .management-actions {
        display: flex;
        flex-direction: row;
        column-gap: map-get($grid-gutter-spacing, 'fifteen');
        align-items: center;

        .btn {
            &:before {
                margin-right: 0;
            }
        }
    }

    .total-records {
        text-align: right;
    }

    .tfoot-bottom {
        display: flex;
        justify-content: space-evenly;
        align-items: center;
        align-content: flex-end;

        .apprentice-pagination {
            margin-top: 0;
        }

        .apprentice-pagination,
        .muted-text {
            margin-left: auto;
        }

        .pagination {
            margin: 0;
        }
    }
}

@media (min-width: map-get($grid-breakpoints, 'sm')) {
    .apprentice-table-header {
        flex-direction: column;

        .apprentice-table-filters {
            > div {
                &:not(:last-child) {
                    margin-right: map-get($grid-gutter-spacing, 'fifteen');
                }
            }

            .per-page-record-select {
                margin-bottom: 0;
            }
        }

        .search {
            flex-grow: 1;
            max-width: 380px;

            .input-group {
                flex-wrap: nowrap;

                input {
                    width: auto;
                }
            }
        }

        .apprentice-data-table-actions {
            flex-direction: row;
            align-items: center;

            > div:not(:last-child),
            > button:not(:last-child),
            > span:not(:last-child),
            > select:not(:last-child) {
                margin-bottom: 0;
            }
        }
    }

    .download-as-excel {
        margin-left: map-get($grid-gutter-spacing, 'fifteen');

        .btn-export-as-excel {
            width: auto;
        }
    }

    ::v-deep .apprentice-data-table {
        white-space: nowrap;
        overflow-x: auto;
    }
}

@media (min-width: map-get($grid-breakpoints, 'md')) {
    .apprentice-table-header {
        flex-wrap: nowrap;

        .apprentice-table-filters {
            align-items: center;
            align-content: center;
            flex: 1 0 100%;
        }

        .apprentice-data-table-actions {
            justify-content: flex-start;
            align-items: center;
            flex-grow: 1;
        }

        .download-as-excel {
            width: auto;
            flex: 1 0 auto;
        }

        .search {
            flex-grow: 1;
        }

        .apprentice-data-table-actions {
            justify-content: flex-start;
            align-items: center;
        }
    }

    ::v-deep .apprentice-data-table {
        white-space: normal;
        overflow-x: auto;
    }
}

@media (min-width: map-get($grid-breakpoints, 'xl')) {
    .apprentice-table-header {
        flex-direction: row;

        .apprentice-table-filters {
            flex: initial;
            margin-bottom: 0;
        }

        .apprentice-data-table-actions {
            flex: initial;
            flex-direction: row;
            justify-content: flex-end;
        }
    }
}
</style>
